export const COMPARISON_MODES = [
  { value: "none", label: "None" },
  { value: "previous_period", label: "Previous Period" },
  { value: "previous_year", label: "Previous Year" },
  { value: "custom", label: "Custom Date Range" },
];

export const COMPARISON_MODE_LABELS = COMPARISON_MODES.reduce((acc, item) => {
  if (item.value !== "none") acc[item.value] = item.label;
  return acc;
}, {});

const parseYmdToUtcDate = (ymd) => {
  const [year, month, day] = String(ymd).split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const formatYmd = (date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const addCalendarDays = (ymd, days) => {
  const date = parseYmdToUtcDate(ymd);
  date.setUTCDate(date.getUTCDate() + days);
  return formatYmd(date);
};

const shiftCalendarYears = (ymd, years) => {
  const date = parseYmdToUtcDate(ymd);
  date.setUTCFullYear(date.getUTCFullYear() + years);
  return formatYmd(date);
};

const inclusiveDaysBetween = (startYmd, endYmd) => {
  const start = parseYmdToUtcDate(startYmd);
  const end = parseYmdToUtcDate(endYmd);
  return Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1;
};

export const computeComparisonRange = (
  mode,
  startDate,
  endDate,
  customStart,
  customEnd
) => {
  const compareMode = String(mode || "").trim();
  if (!compareMode || compareMode === "none") return null;

  if (compareMode === "custom") {
    if (!customStart || !customEnd) return null;
    return {
      mode: compareMode,
      label: COMPARISON_MODE_LABELS.custom,
      startDate: String(customStart).trim(),
      endDate: String(customEnd).trim(),
    };
  }

  if (!startDate || !endDate) return null;

  const mainStart = String(startDate).trim();
  const mainEnd = String(endDate).trim();
  const dayBeforeStart = addCalendarDays(mainStart, -1);

  if (compareMode === "previous_period") {
    const duration = inclusiveDaysBetween(mainStart, mainEnd);
    return {
      mode: compareMode,
      label: COMPARISON_MODE_LABELS.previous_period,
      startDate: addCalendarDays(dayBeforeStart, -(duration - 1)),
      endDate: dayBeforeStart,
    };
  }

  if (compareMode === "previous_year") {
    return {
      mode: compareMode,
      label: COMPARISON_MODE_LABELS.previous_year,
      startDate: shiftCalendarYears(mainStart, -1),
      endDate: shiftCalendarYears(mainEnd, -1),
    };
  }

  return null;
};

export const formatBreakdownDateLabel = (ymd) => {
  if (!ymd) return "";
  const [year, month, day] = String(ymd).split("-");
  if (!year || !month || !day) return String(ymd);
  return `${day}-${month}-${year}`;
};

export const formatBreakdownDateRangeLabel = (startDate, endDate) => {
  if (!startDate || !endDate) return "";
  return `${formatBreakdownDateLabel(startDate)} to ${formatBreakdownDateLabel(endDate)}`;
};

export const calcPercentChange = (current, previous) => {
  const cur = Number(current) || 0;
  const prev = Number(previous) || 0;

  if (prev === 0) {
    if (cur === 0) {
      return { value: 0, direction: "flat", display: "0.00%" };
    }
    return { value: 100, direction: "up", display: "100.00%" };
  }

  const pct = ((cur - prev) / prev) * 100;
  const direction = pct > 0 ? "up" : pct < 0 ? "down" : "flat";
  return {
    value: pct,
    direction,
    display: `${Math.abs(pct).toFixed(2)}%`,
  };
};

export const formatSignedDiff = (current, previous, formatValue = (v) => String(v)) => {
  const diff = (Number(current) || 0) - (Number(previous) || 0);
  if (diff === 0) return formatValue(0);
  if (diff > 0) {
    const formatted = formatValue(diff);
    return formatted.startsWith("+") ? formatted : `+${formatted}`;
  }
  return formatValue(diff);
};
